import './motion.css';
import axiosInstance from './api/axios';
import { preloadNavigation } from './api/navigationCache';
import { useState, useCallback, useRef, useMemo, useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import AuthPage from './components/AuthPage';
import LandingPage from './components/LandingPage';
import PublicShell from './components/PublicShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import './App.css';
import PullToRefresh from './components/PullToRefresh';

// Lazy-load all heavy components (only fetched after login)
const Navbar = lazy(() => import('./components/Navbar'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AssignmentList = lazy(() => import('./components/AssignmentList'));
const CustomerList = lazy(() => import('./components/CustomerList'));
const InvoiceList = lazy(() => import('./components/InvoiceList'));
const Statistics = lazy(() => import('./components/Statistics'));
const ProfileSettings = lazy(() => import('./components/ProfileSettings'));
const EmailClient = lazy(() => import('./components/EmailClient'));
const Buchfuehrung = lazy(() => import('./components/Buchfuehrung'));
const TaxAssistant = lazy(() => import('./components/TaxAssistant'));
const Settings = lazy(() => import('./components/Settings'));
const Help = lazy(() => import('./components/Help'));
const DocumentsFolder = lazy(() => import('./components/DocumentsFolder'));
const FeedbackDialog = lazy(() => import('./components/FeedbackDialog'));
const BetaFeedbackButton = lazy(() => import('./components/BetaFeedbackButton')); // eslint-disable-line no-unused-vars
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const RemoteSignaturePage = lazy(() => import('./components/RemoteSignaturePage'));
const CustomerAssignmentPage = lazy(() => import('./components/CustomerAssignmentPage'));
const DocumentSignaturePage = lazy(() => import('./components/DocumentSignaturePage'));
const BankingPage = lazy(() => import('./components/BankingPage'));
const GuidedTour = lazy(() => import('./components/GuidedTour'));
const WorkflowTourSurface = lazy(() => import('./components/WorkflowTourSurface'));
import { WORKFLOW_TOURS } from './components/workflowTours';

// OnboardingWizard needs named export, import separately
const OnboardingWizardLazy = lazy(() => import('./components/OnboardingWizard'));

// Preload JS chunks without mounting components (warms webpack cache)
const preloadChunks = () => {
  const chunks = [
    () => import('./components/AssignmentList'),
    () => import('./components/CustomerList'),
    () => import('./components/InvoiceList'),
    () => import('./components/Buchfuehrung'),
    () => import('./components/EmailClient'),
    () => import('./components/Statistics'),
    () => import('./components/DocumentsFolder'),
    () => import('./components/Settings'),
    () => import('./components/Help'),
    () => import('./components/ProfileSettings'),
    () => import('./components/AdminPanel'),
  ];
  // Stagger chunk downloads to avoid network congestion
  chunks.forEach((load, i) => {
    setTimeout(() => load().catch(() => {}), i * 150);
  });
};

// Inline onboarding check (avoids importing the heavy OnboardingWizard eagerly)
const useOnboarding = () => {
  const shouldShow = localStorage.getItem('show_onboarding') === 'true';
  const reset = () => localStorage.setItem('show_onboarding', 'true');
  return { shouldShow, reset };
};

// Minimal spinner for Suspense fallback while lazy chunks load
const TabSpinner = () => (
  <div className="flex items-center justify-center py-24">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400"></div>
  </div>
);

function App() {
  const { user, loading, isAuthenticated } = useAuth();
  const { theme } = useTheme();

  // Aktiven Tab aus URL-Hash oder localStorage wiederherstellen
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '').split('?')[0];
    if (hash && ['dashboard', 'assignments', 'invoices', 'customers', 'email', 'buchfuehrung', 'tax-assistant', 'statistics', 'documents', 'help', 'settings', 'profile', 'admin', 'banking'].includes(hash)) {
      return hash;
    }
    const saved = localStorage.getItem('activeTab');
    // Always start with dashboard on page load (fresh login or window reopen)
    if (saved) {
      localStorage.setItem('activeTab', 'dashboard');
    }
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [tabResetKey, setTabResetKey] = useState(0);
  const [navigationHistory, setNavigationHistory] = useState([getInitialTab()]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [prefillAssignment, setPrefillAssignment] = useState(null);
  const [mailImportText, setMailImportText] = useState(null);
  const [editAssignmentFromDashboard, setEditAssignmentFromDashboard] = useState(null);
  const [notificationAssignmentId, setNotificationAssignmentId] = useState(null);
  const [notificationInvoiceId, setNotificationInvoiceId] = useState(null);
  const [pendingEmailCompose, setPendingEmailCompose] = useState(null);
  const [activeWorkflowTour, setActiveWorkflowTour] = useState(null);
  const [tourLocation, setTourLocation] = useState(null);
  const navigateTour = useCallback((location) => {
    setTourLocation(location);
    setActiveTab(location.page);
    setVisitedTabs(previous => new Set([...previous, location.page]));
    localStorage.setItem('activeTab', location.page);
    window.history.replaceState(null, '', `#${location.page}`);
  }, []);
  const closeWorkflowTour = useCallback(() => {
    setActiveWorkflowTour(null);
    setTourLocation(null);
  }, []);
  const [pendingSettingsTab, setPendingSettingsTab] = useState(null);

  // Preserve valid deep links when restoring an authenticated session.
  const prevUserRef = useRef(null);
  useEffect(() => {
    if (user && !prevUserRef.current) {
      const initialTab = getInitialTab();
      setActiveTab(initialTab);
      setVisitedTabs(prev => new Set([...prev, initialTab]));
      localStorage.setItem('activeTab', initialTab);
    }
    prevUserRef.current = user;
  }, [user]);

  // Browser-Back/Forward Support: hashchange-Listener synchronisiert activeTab
  // mit URL-Hash. Damit funktionieren die Browser-Pfeile (zurück/vor) sauber.
  useEffect(() => {
    if (!user) return;
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '').split('?')[0];
      const allowedTabs = ['dashboard', 'assignments', 'invoices', 'customers', 'email', 'buchfuehrung', 'documents', 'settings', 'help', 'profile', 'admin', 'tax-assistant', 'banking', 'statistics'];
      if (hash && allowedTabs.includes(hash) && hash !== activeTab) {
        setActiveTab(hash);
        setVisitedTabs(prev => new Set([...prev, hash]));
        localStorage.setItem('activeTab', hash);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [user, activeTab]);
  const { shouldShow: showOnboarding, reset: resetOnboarding } = useOnboarding();
  const [onboardingVisible, setOnboardingVisible] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(['dashboard', getInitialTab()]));

  // A direct #admin link must not mount the admin panel for ordinary users.
  useEffect(() => {
    if (!loading && user?.role !== 'admin' && activeTab === 'admin') {
      setActiveTab('dashboard');
      localStorage.setItem('activeTab', 'dashboard');
      window.history.replaceState(null, '', '#dashboard');
    }
  }, [loading, user?.role, activeTab]);

  // Browser-like navigation: Every click adds to history
  const navigateTo = useCallback((newTab, subTab = null) => {
    // Wenn Settings mit Sub-Tab gewünscht ist, merken
    if (newTab === 'settings' && subTab) {
      setPendingSettingsTab(subTab);
    }
    // Gleicher Tab erneut geklickt → Reset zur Listenansicht (außer Dashboard)
    if (newTab === activeTab && newTab !== 'dashboard') {
      setTabResetKey(prev => prev + 1);
      return;
    }
    setNavigationHistory(prev => {
      const newHistory = [...prev.slice(0, historyIndex + 1), newTab];
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
    setActiveTab(newTab);
    setVisitedTabs(prev => {
      const next = new Set(prev);
      next.add(newTab);
      return next;
    });
    localStorage.setItem('activeTab', newTab);
    window.location.hash = newTab;
  }, [historyIndex, activeTab]);

  // Kalender → Einsatz erstellen: Daten übergeben und zur Einsatz-Seite navigieren
  const handleCreateFromCalendar = useCallback((assignmentData) => {
    setPrefillAssignment(assignmentData);
    navigateTo('assignments');
  }, [navigateTo]);

  const handleCreateFromEmail = useCallback((text) => {
    setMailImportText(text);
    navigateTo('assignments');
  }, [navigateTo]);

  // Heutige Einsätze → Einsatz bearbeiten
  const handleEditFromDashboard = useCallback((assignmentData) => {
    setEditAssignmentFromDashboard(assignmentData);
    navigateTo('assignments');
  }, [navigateTo]);

  // Notification click → je nach Typ zur passenden Seite navigieren
  const handleNotificationClick = useCallback((notification) => {
    const type = notification.type;
    // Steuerassistent-Erinnerungen
    if (type === 'tax_monthly' || type === 'tax_yearend') {
      navigateTo('tax-assistant');
      return;
    }
    // Aggregierte Überfällig-Erinnerung → Rechnungen → Offen-Tab → Überfällig-Filter
    if (type === 'invoices_overdue_daily' || notification.related_type === 'invoices_overdue') {
      setNotificationInvoiceId('__overdue__');
      navigateTo('invoices');
      return;
    }
    // Rechnung-bezogen (versendet, Mahnung, etc.)
    if (notification.related_type === 'invoice' && notification.related_id) {
      setNotificationInvoiceId(notification.related_id);
      navigateTo('invoices');
      return;
    }
    // Einsatz-bezogen (Fernsignatur, Beleg-Erinnerung)
    if (notification.related_type === 'assignment' && notification.related_id) {
      setNotificationAssignmentId(notification.related_id);
      navigateTo('assignments');
    }
  }, [navigateTo]);

  // Go back to previous page (browser-like)
  const goBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const tab = navigationHistory[newIndex];
      setActiveTab(tab);
      setVisitedTabs(prev => { const n = new Set(prev); n.add(tab); return n; });
      localStorage.setItem('activeTab', tab);
      window.location.hash = tab;
    }
  }, [historyIndex, navigationHistory]);

  const canGoBack = historyIndex > 0;

  // Global email compose trigger - switch to email tab with pre-filled data
  const handleEmailCompose = useCallback((composeData) => {
    setPendingEmailCompose(composeData);
    navigateTo('email');
  }, [navigateTo]);

  // Mobile swipe-back gesture (left edge swipe → go back)
  const touchStartRef = useRef(null);
  useEffect(() => {
    const isMobile = () => window.innerWidth < 768;
    const handleTouchStart = (e) => {
      if (!isMobile() || !canGoBack) return;
      const touch = e.touches[0];
      if (touch.clientX < 30) {
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };
    const handleTouchEnd = (e) => {
      if (!touchStartRef.current) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = Math.abs(touch.clientY - touchStartRef.current.y);
      touchStartRef.current = null;
      if (dx > 80 && dy < 100) goBack();
    };
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [canGoBack, goBack]);

  const handlePrefillConsumed = useCallback(() => setPrefillAssignment(null), []);
  const handleEditConsumed = useCallback(() => setEditAssignmentFromDashboard(null), []);

  // Start onboarding tour without page reload
  const startTour = useCallback(() => {
    localStorage.removeItem('onboarding_completed');
    navigateTo('dashboard');
    // Small delay so dashboard renders before tour overlay appears
    setTimeout(() => setOnboardingVisible(true), 300);
  }, [navigateTo]);
  useEffect(() => {
    if (isAuthenticated && showOnboarding) {
      setOnboardingVisible(true);
    }
  }, [isAuthenticated, showOnboarding]);

  // Download code ahead of time, but mount data-fetching screens only on demand.
  // Mounting every hidden screen caused request bursts and 429s on mobile reloads.
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = setTimeout(() => preloadChunks(), 800);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Warm only the core lists, sequentially, after the initial screen is ready.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const timer = setTimeout(() => preloadNavigation(axiosInstance, () => cancelled), 1800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isAuthenticated, user?.id]);

  // Memoized tab components to prevent unnecessary re-renders
  const dashboardTab = useMemo(() => (
    <Dashboard onCreateFromCalendar={handleCreateFromCalendar} onEditAssignment={handleEditFromDashboard} isActive={activeTab === 'dashboard'} onNavigate={navigateTo} onStartWorkflowTour={setActiveWorkflowTour} />
  ), [handleCreateFromCalendar, handleEditFromDashboard, activeTab, navigateTo]);

  const handleNotificationConsumed = useCallback(() => setNotificationAssignmentId(null), []);
  const handleInvoiceNotificationConsumed = useCallback(() => setNotificationInvoiceId(null), []);

  const assignmentsTab = useMemo(() => (
    <AssignmentList prefillAssignment={prefillAssignment} onPrefillConsumed={handlePrefillConsumed} mailImportText={mailImportText} onMailImportConsumed={() => setMailImportText(null)} editAssignment={editAssignmentFromDashboard} onEditConsumed={handleEditConsumed} notificationAssignmentId={notificationAssignmentId} onNotificationConsumed={handleNotificationConsumed} tabResetKey={tabResetKey} isActive={activeTab === 'assignments'} />
  ), [prefillAssignment, mailImportText, editAssignmentFromDashboard, notificationAssignmentId, tabResetKey, handlePrefillConsumed, handleEditConsumed, handleNotificationConsumed, activeTab]);

  const customersTab = useMemo(() => <CustomerList tabResetKey={tabResetKey} isActive={activeTab === 'customers'} />, [tabResetKey, activeTab]);
  const invoicesTab = useMemo(() => <InvoiceList tabResetKey={tabResetKey} isActive={activeTab === 'invoices'} onEmailCompose={handleEmailCompose} onEditAssignment={handleEditFromDashboard} notificationInvoiceId={notificationInvoiceId} onNotificationConsumed={handleInvoiceNotificationConsumed} />, [tabResetKey, activeTab, handleEmailCompose, handleEditFromDashboard, notificationInvoiceId, handleInvoiceNotificationConsumed]);
  const emailTab = useMemo(() => <EmailClient tabResetKey={tabResetKey} pendingCompose={pendingEmailCompose} onPendingConsumed={() => setPendingEmailCompose(null)} onCreateAssignmentFromEmail={handleCreateFromEmail} />, [tabResetKey, pendingEmailCompose, handleCreateFromEmail]);
  const buchfuehrungTab = useMemo(() => <Buchfuehrung tabResetKey={tabResetKey} isActive={activeTab === 'buchfuehrung'} onNavigate={navigateTo} />, [tabResetKey, activeTab, navigateTo]);
  const taxAssistantTab = useMemo(() => <TaxAssistant tabResetKey={tabResetKey} />, [tabResetKey]);
  const statisticsTab = useMemo(() => <Statistics tabResetKey={tabResetKey} isActive={activeTab === 'statistics'} />, [tabResetKey, activeTab]);
  const documentsTab = useMemo(() => <DocumentsFolder tabResetKey={tabResetKey} />, [tabResetKey]);
  const settingsTab = useMemo(() => <Settings tabResetKey={tabResetKey} initialTab={pendingSettingsTab} onInitialTabConsumed={() => setPendingSettingsTab(null)} onStartWorkflowTour={setActiveWorkflowTour} />, [tabResetKey, pendingSettingsTab]);
  const helpTab = useMemo(() => <Help tabResetKey={tabResetKey} onStartTour={startTour} onStartWorkflowTour={setActiveWorkflowTour} onNavigate={navigateTo} />, [tabResetKey, startTour, navigateTo]);
  const profileTab = useMemo(() => <ProfileSettings tabResetKey={tabResetKey} />, [tabResetKey]);
  const adminTab = useMemo(() => user?.role === 'admin' ? <AdminPanel onBack={() => navigateTo('dashboard')} /> : null, [navigateTo, user?.role]);
  const bankingTab = useMemo(() => <BankingPage tabResetKey={tabResetKey} />, [tabResetKey]);

  const tabMap = useMemo(() => ({
    dashboard: dashboardTab,
    assignments: assignmentsTab,
    customers: customersTab,
    invoices: invoicesTab,
    email: emailTab,
    buchfuehrung: buchfuehrungTab,
    'tax-assistant': taxAssistantTab,
    statistics: statisticsTab,
    documents: documentsTab,
    settings: settingsTab,
    help: helpTab,
    profile: profileTab,
    admin: adminTab,
    banking: bankingTab,
  }), [dashboardTab, assignmentsTab, customersTab, invoicesTab, emailTab, buchfuehrungTab, taxAssistantTab, statisticsTab, documentsTab, settingsTab, helpTab, profileTab, adminTab, bankingTab]);

  const tabsContainerRef = useRef(null);

  // Start before paint: never show a full-opacity frame before the fade.
  useLayoutEffect(() => {
    const wrapper = tabsContainerRef.current?.querySelector(`[data-tab="${activeTab}"]`);
    if (!wrapper || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const animation = wrapper.animate?.([{ opacity: .8 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    return () => animation?.cancel();
  }, [activeTab]);

  const renderTabs = useCallback(() => {
    return Object.entries(tabMap).map(([key, component]) => (
      visitedTabs.has(key) ? (
        <div key={key} data-tab={key} data-io-motion-root="" style={{ display: activeTab === key ? 'block' : 'none' }}>
          <ErrorBoundary>
            <Suspense fallback={activeTab === key ? <TabSpinner /> : null}>
              {component}
            </Suspense>
          </ErrorBoundary>
        </div>
      ) : null
    ));
  }, [tabMap, visitedTabs, activeTab]);

  // Public route: Remote Signature (no auth required)
  const customerAssignmentMatch = window.location.pathname.match(/^\/customer-assignment\/([^/]+)\/?$/);
  if (customerAssignmentMatch) {
    return <Suspense fallback={<TabSpinner />}><CustomerAssignmentPage token={customerAssignmentMatch[1]} /></Suspense>;
  }
  const documentSignMatch = window.location.pathname.match(/^\/document-sign\/([^/]+)\/?$/);
  if (documentSignMatch) {
    return <Suspense fallback={<TabSpinner />}><DocumentSignaturePage token={documentSignMatch[1]} /></Suspense>;
  }
  const signMatch = window.location.pathname.match(/\/sign\/(.+)/);
  if (signMatch) {
    return (
      <Suspense fallback={<TabSpinner />}>
        <RemoteSignaturePage token={signMatch[1]} />
      </Suspense>
    );
  }

  // Bank-Callback vom Enable-Banking-Portal: /bank/callback?code=...&state=...
  // Kubernetes-Ingress routet nur /api/* zum Backend — daher weiterleiten.
  if (window.location.pathname === '/bank/callback') {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.location.replace(`${backendUrl}/api/banking/callback${window.location.search}`);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // URL-based routing (kein react-router):
    //   `/`, `/login`, `/register`, `/reset-password` → AuthPage
    //   `/info`, `/mehr-infos`, `/start`              → LandingPage
    //   `?verify=…` → PublicShell (AuthPage mit Logo), `?reset-token=…` → AuthPage
    // Beide Public-Views leben in `PublicShell` mit horizontaler Slide-Animation.
    // Passwort-Reset hat ein eigenes Layout (ohne Shared-Brand); Verify-Links laufen
    // über die PublicShell, damit IO-Logo + Wortmarke sichtbar bleiben.
    const isReset =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('reset-token');
    if (isReset) {
      return <AuthPage />;
    }
    return <PublicShell />;
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black' : 'bg-white'}`} data-testid="main-app">
      {!activeWorkflowTour && <PullToRefresh />}
      <div inert={activeWorkflowTour ? true : undefined}><Suspense fallback={null}>
        <Navbar activeTab={activeTab} setActiveTab={navigateTo} onNotificationClick={handleNotificationClick} />
      </Suspense></div>
      
      <main ref={tabsContainerRef} className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 pb-32 sm:pb-24">
      {canGoBack && !activeWorkflowTour && (
        <button
          onClick={goBack}
          className="io-back-button flex w-10 h-10 rounded-full bg-blue-600 dark:bg-amber-500 hover:bg-blue-700 dark:hover:bg-amber-600 text-white dark:text-black shadow-lg hover:shadow-xl transition-all duration-200 items-center justify-center group border-0"
          aria-label="Zurück"
          title=""
          data-testid="back-button"
        >
          <svg 
            viewBox="0 0 24 24" 
            className="w-6 h-6 scale-110"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span className="absolute left-14 bg-gray-800 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Zurück
          </span>
        </button>
      )}
      
        <div style={{ display: activeWorkflowTour ? 'none' : undefined }}>{renderTabs()}</div>
        {activeWorkflowTour && (
          <Suspense fallback={<TabSpinner />}>
            <WorkflowTourSurface key={activeWorkflowTour} location={tourLocation} />
          </Suspense>
        )}
      </main>
      <Suspense fallback={null}>
        <FeedbackDialog />
      </Suspense>
      {onboardingVisible && (
        <Suspense fallback={null}>
          <OnboardingWizardLazy
            onClose={() => setOnboardingVisible(false)}
            onNavigate={navigateTo}
          />
        </Suspense>
      )}
      {activeWorkflowTour && WORKFLOW_TOURS[activeWorkflowTour] && (
        <Suspense fallback={null}>
          <GuidedTour
            key={activeWorkflowTour}
            steps={WORKFLOW_TOURS[activeWorkflowTour].steps}
            icon={WORKFLOW_TOURS[activeWorkflowTour].icon}
            tourTitle={WORKFLOW_TOURS[activeWorkflowTour].tourTitle}
            onClose={closeWorkflowTour}
            onNavigate={navigateTour}
          />
        </Suspense>
      )}
    </div>
  );
}

export default App;
